import axios from "axios";
import CryptoJS from "crypto-js";
import dayjs from "dayjs";
import { safeDecrypt } from "../modules/uwin/components/uwin-decrypt";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
export { getUwinClientToken } from '../services/uwin.api';

/**
 * Convert date from D/ DD-MMM-YYYY format to YYYY-MM-DD format for API
 * @param {string} dateString - Date string like "1-JAN-2000" or "01-Jan-2000"
 * @returns {string} Date in YYYY-MM-DD format or empty string if invalid
 */
export const formatDobForAPI = (dateString) => {
    if (!dateString) return "";

    try {
        // Normalize month casing so JAN / jan / Jan all work
        const normalizedDate = dateString
            .toLowerCase()
            .replace(/-(\w{3})-/, (_, m) => `-${m[0].toUpperCase()}${m.slice(1)}-`);
        // Example: "1-JAN-2000" -> "1-Jan-2000"

        // Accept both single-digit and double-digit day formats
        const parsedDate = dayjs(
            normalizedDate,
            ["D-MMM-YYYY", "DD-MMM-YYYY"], // Accept both formats
            true // strict parsing
        );

        if (!parsedDate.isValid()) {
            console.error("Invalid date format:", dateString);
            return "";
        }

        // Convert to API-required format
        return parsedDate.format("YYYY-MM-DD");
    } catch (error) {
        console.error("Error formatting date:", error);
        return "";
    }
};

/**
 * Convert date from YYYY-MM-DD format to DD-MMM-YYYY format for display
 * @param {string} dateString - Date string like "2025-01-01"
 * @returns {string} Date in DD-MMM-YYYY format or empty string if invalid
 */
export const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";

    try {
        const parsedDate = dayjs(dateString, "YYYY-MM-DD", true);

        if (!parsedDate.isValid()) {
            console.error("Invalid date format:", dateString);
            return "";
        }

        // ✅ Direct format using dayjs
        return parsedDate.format("YYYY-MM-DD");

    } catch (error) {
        console.error("Error formatting date:", error);
        return "";
    }
};

export const encryptText = async (plaintext) => {
    try {
        const accessToken = localStorage.getItem('Backend_token');
        const resEncrypt = await axios.post("nodeapi/uwin/encryption", {
            plaintext: plaintext
        }, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${accessToken}`,
            }
        });
        console.log("Encryption response:", resEncrypt.data.data.encrypted);
        return resEncrypt.data.data.encrypted;
    } catch (error) {
        console.error("Backend login error:", error);
    }
};

export const calculateAge = (dob) => {
    if (!dob) {
        return {
            age: null,
            ageText: "",
        };
    }

    const birthDate = new Date(dob);
    const today = new Date();

    let months =
        (today.getFullYear() - birthDate.getFullYear()) * 12 +
        (today.getMonth() - birthDate.getMonth());

    if (today.getDate() < birthDate.getDate()) {
        months--;
    }

    // Less than 1 year
    if (months < 12) {
        return {
            age: months,
            ageText: `${months} ${months === 1 ? "month" : "months"}`,
        };
    }

    // 1 year or more
    const years = Math.floor(months / 12);

    return {
        age: years,
        ageText: `${years} ${years === 1 ? "year" : "years"}`,
    };
};

export const maskAbhaNumber = (number) => {
    if (!number) return "";

    // Remove all non-numeric characters
    const cleaned = number.toString().replace(/\D/g, "");

    // Minimum 12 digits check
    if (cleaned.length < 12) {
        return cleaned;
    }

    // Keep all except last 4 digits
    const visiblePart = cleaned.slice(0, -4);
    const maskedPart = "****";

    // Add dash after every 4 digits only in visible part
    const formattedVisible = visiblePart.match(/.{1,4}/g).join("-");

    return `${formattedVisible}${maskedPart}`;
};

export const decryptOpenSSLAES = (encryptedBase64) => {
    const passphrase = "AGGRE@$*&#(!@%^&";

    if (!encryptedBase64) return null;

    const data = CryptoJS.enc.Base64.parse(encryptedBase64);

    const header = CryptoJS.enc.Utf8.stringify(
        CryptoJS.lib.WordArray.create(data.words.slice(0, 2))
    );

    if (header !== "Salted__") {
        throw new Error("Invalid encrypted OpenSSL data");
    }

    const salt = CryptoJS.lib.WordArray.create(data.words.slice(2, 4));

    const ciphertext = CryptoJS.lib.WordArray.create(data.words.slice(4));

    let keyIv = CryptoJS.lib.WordArray.create();
    let last = CryptoJS.lib.WordArray.create();

    while (keyIv.sigBytes < 48) {
        last = CryptoJS.MD5(
            CryptoJS.lib.WordArray.create([
                ...last.words,
                ...CryptoJS.enc.Utf8.parse(passphrase).words,
                ...salt.words,
            ])
        );
        keyIv = CryptoJS.lib.WordArray.create([...keyIv.words, ...last.words]);
    }

    const key = CryptoJS.lib.WordArray.create(keyIv.words.slice(0, 8));   // 32 bytes
    const iv = CryptoJS.lib.WordArray.create(keyIv.words.slice(8, 12));   // 16 bytes

    const decrypted = CryptoJS.AES.decrypt(
        { ciphertext },
        key,
        {
            iv: iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7
        }
    );

    return decrypted.toString(CryptoJS.enc.Utf8);
};

/**
 * Generic Blob Download Function
 * @param {Blob} blob - The blob data to save
 * @param {string} fileName - Base name of the file
 * @param {string} extension - File extension (e.g., 'pdf', 'xlsx')
 */
export const saveBlobToFile = (blob, fileName, extension) => {
    try {
        if (blob && (blob instanceof Blob || blob.size > 0)) {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `${fileName}.${extension}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            return true;
        }
        return false;
    } catch (error) {
        console.error("Save blob error:", error);
        return false;
    }
};

/**
 * Date Display Helper - Shows "Today", "Tomorrow", or formatted date
 *
 * Takes display_time (e.g., "25 Feb 2026 (6:00 AM - 11:30 PM)")
 * and formats it to be more user-friendly
 */
export const formatRelativeDateDisplay = (displayTime) => {
    if (!displayTime) return 'Date not available';

    try {
        // Extract the date part from display_time (before the parentheses)
        const dateMatch = displayTime.match(/^(.+?)\s*\(/);
        const datePart = dateMatch ? dateMatch[1].trim() : displayTime;

        // Parse the date (assuming format like "25 Feb 2026")
        const sessionDate = new Date(datePart);
        const today = new Date();
        const tomorrow = new Date();
        tomorrow.setDate(today.getDate() + 1);

        // Normalize dates to ignore time for comparison
        const normalizeDate = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

        const sessionDateNorm = normalizeDate(sessionDate);
        const todayNorm = normalizeDate(today);
        const tomorrowNorm = normalizeDate(tomorrow);

        // Extract time part (inside parentheses)
        const timeMatch = displayTime.match(/\((.+)\)/);
        const timePart = timeMatch ? timeMatch[1] : '';

        if (sessionDateNorm.getTime() === todayNorm.getTime()) {
            return timePart ? `Today (${timePart})` : 'Today';
        } else if (sessionDateNorm.getTime() === tomorrowNorm.getTime()) {
            return timePart ? `Tomorrow (${timePart})` : 'Tomorrow';
        } else {
            // Return original display_time for other dates
            return displayTime;
        }
    } catch (error) {
        // If date parsing fails, return original display_time
        console.warn('Date parsing failed for:', displayTime, error);
        return displayTime;
    }
};

/**
* Age Range Business Logic - Updated with decryption and simplified campaign logic
*
* Business Rules:
* - If decrypt(uip_session_category_id) === "4" → ">=14Y & 15Y 90D"
* - If is_campaign_session === true → "All Age" (simplified)
* - If uip_session_category === "RI - Pregnant Women" → "12-49Y"
* - Else → "All Age"
*
* Error Handling:
* - Decryption failures gracefully fall through to other rules
* - Missing is_campaign_session field treated as false
*/
export const formatAgeRangeDisplay = (sessionData) => {
    // Input validation - return fallback if no data
    if (!sessionData || typeof sessionData !== 'object') {
        return 'All Age';
    }

    const {
        uip_session_category_id,
        uip_session_category,
        is_campaign_session,
        campaign_min_age,
        campaign_max_age
    } = sessionData;

    // Rule 1: Adolescent session (decrypt ID and check if equals "4")
    try {
        const decryptedId = safeDecrypt(uip_session_category_id);
        if (decryptedId === "4") {
            return '>=14Y & 15Y 90D';
        }
    } catch (error) {
        // If decryption fails, log error and continue to other rules
        console.warn('Failed to decrypt uip_session_category_id for age range:', error);
    }

    // Rule 2: Campaign session → show campaign age bounds
    if (is_campaign_session === true) {
        if (campaign_min_age && campaign_max_age) {
            return `${campaign_min_age} - ${campaign_max_age}`;
        }
        return 'All Age';
    }

    // Rule 3: Pregnant Women session - standard reproductive age range
    if (uip_session_category === 'RI - Pregnant Women') {
        return '12-49Y';
    }

    // Rule 4: Default - all ages welcome
    return 'All Age';
};

/**
 * Session Name Business Logic - Updated with decryption and simplified campaign logic
 *
 * Business Rules:
 * - If decrypt(uip_session_category_id) === "4" → "{uip_session_category} - Adolescent"
 * - If is_campaign_session === true → "Campaign Session" (simplified)
 * - Else → "{uip_session_category}"
 *
 * Error Handling:
 * - Decryption failures are handled by safeDecrypt (returns "-" or original value)
 * - Missing fields default to safe fallbacks
 */
export const formatSessionNameDisplay = (sessionData) => {
    // Input validation - return fallback if no data
    if (!sessionData || typeof sessionData !== 'object') {
        return 'Unknown Session';
    }

    const {
        uip_session_category_id,
        uip_session_category,
        is_campaign_session,
        campaign_name
    } = sessionData;

    // Rule 1: Adolescent session (decrypt ID and check if equals "4")
    // Note: After decryption, the ID becomes a string, so we compare against "4"
    try {
        const decryptedId = safeDecrypt(uip_session_category_id);
        if (decryptedId === "4") {
            const baseCategory = uip_session_category || 'Session';
            return `${baseCategory} - Adolescent`;
        }
    } catch (error) {
        // If decryption fails, log error and continue to other rules
        console.warn('Failed to decrypt uip_session_category_id:', error);
    }

    // Rule 2: Campaign session → "Campaign Session - {campaign_name}"
    if (is_campaign_session === true) {
        return campaign_name ? `Campaign Session - ${campaign_name}` : 'Campaign Session';
    }

    // Rule 3: Default - use session category as-is
    return uip_session_category || 'Unknown Session';
};

/**
 * Beneficiary Age Display Helper
 * Formats age based on category and DOB. Children get detailed '1y 10d' format, adults get '26 yr'.
 */
export const getBeneficiaryAgeDisplay = (beneficiaryData) => {
    if (!beneficiaryData) return "NA";

    const age = beneficiaryData.age;
    const dob = beneficiaryData.dob;
    const category = beneficiaryData.beneficiary_type_name || "";
    const isChildCategory = ["child", "infant", "new born", "newborn"].some(c => category.toLowerCase().includes(c));

    // If it's a child category and we have DOB, calculate detailed age
    if (isChildCategory && dob) {
        const birthDate = new Date(dob);
        if (!isNaN(birthDate.getTime())) {
            const today = new Date();
            let years = today.getFullYear() - birthDate.getFullYear();
            let months = today.getMonth() - birthDate.getMonth();
            let days = today.getDate() - birthDate.getDate();

            if (days < 0) {
                months--;
                const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                days += prevMonth.getDate();
            }
            if (months < 0) {
                years--;
                months += 12;
            }

            let ageParts = [];
            if (years > 0) ageParts.push(`${years}y`);
            if (months > 0) ageParts.push(`${months}m`);
            if (days > 0 || (years === 0 && months === 0)) ageParts.push(`${days}d`);

            if (ageParts.length > 0) {
                return ageParts.join(" ");
            }
        }
    }

    // Default formatting for adults or if dob is invalid/missing
    return `${age ?? "NA"} yr`;
};

export const isTestingDateOlderThan30Days = (testingDate) => {
    if (!testingDate) return true;

    const today = new Date();
    const testDate = new Date(testingDate);

    if (isNaN(testDate)) return false;

    const diffTime = today.getTime() - testDate.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays >= 30;
};

/**
 * Return the visible suffix of a photo ID for display (type/suffix format).
 * If the value is masked with asterisks (e.g. "****4567"), returns text after the last "*".
 * If unmasked, returns the last 4 characters.
 * @param {string|number} value - Plain-text or masked ID number
 * @returns {string} Visible suffix only
 */
export const getVisiblePhotoIdSuffix = (value) => {
    const str = String(value ?? "");
    if (!str) return "";

    const lastAsteriskIndex = str.lastIndexOf("*");
    if (lastAsteriskIndex !== -1) {
        return str.slice(lastAsteriskIndex + 1);
    }

    if (str.length <= 4) return str;
    return str.slice(-4);
};